import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CheckCircle, X, MagnifyingGlass, ArrowClockwise } from "@phosphor-icons/react";
import { formatIdDate } from "@/lib/format";

const emptyFilters = {
  date_from: "",
  date_to: "",
  sync_status: "all",
  distrik_id: "",
  pelanggan: "",
  no_so: "",
  group_by: "none",
};

export default function SyncMatchReport() {
  const [filters, setFilters] = useState(emptyFilters);
  const [data, setData] = useState({ rows: [], groups: [], counts: {} });
  const [districts, setDistricts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await api.get("/districts");
        setDistricts(d || []);
      } catch (err) {
        // Districts filter is optional — page renders without it. Log in dev.
        if (process.env.NODE_ENV === "development") console.warn("districts fetch failed:", err);
      }
    })();
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (v && v !== "all" && v !== "none") params[k] = v;
      });
      // group_by "none" is fine; keep it so backend knows we skipped grouping.
      params.group_by = filters.group_by;
      const { data: r } = await api.get("/reports/sync-match", { params });
      setData(r);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const badge = (status, reasons = []) => {
    if (status === "matched")
      return <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase bg-emerald-100 text-emerald-800 border border-emerald-300">✓ Match</span>;
    if (status === "mismatch")
      return (
        <span
          className="px-1.5 py-0.5 text-[10px] font-bold uppercase bg-red-100 text-red-800 border border-red-300"
          title={reasons.join(", ")}
        >
          ✗ Mismatch
        </span>
      );
    return <span className="px-1.5 py-0.5 text-[10px] uppercase bg-secondary text-muted-foreground border border-border">— Belum</span>;
  };

  return (
    <div className="p-6 space-y-4">
      <div>
        <div className="overline">Laporan Sync Match</div>
        <h1 className="text-3xl font-black tracking-tight">SPB vs Google Sheets</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Cek SPB mana yang cocok dan tidak cocok dengan realisasi di Google Sheet.
        </p>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 border border-border p-3 bg-secondary/30" data-testid="sync-match-filters">
        <div>
          <Label className="text-[10px] uppercase">Dari</Label>
          <Input type="date" value={filters.date_from} onChange={(e) => setF("date_from", e.target.value)} className="rounded-none h-9" />
        </div>
        <div>
          <Label className="text-[10px] uppercase">Sampai</Label>
          <Input type="date" value={filters.date_to} onChange={(e) => setF("date_to", e.target.value)} className="rounded-none h-9" />
        </div>
        <div>
          <Label className="text-[10px] uppercase">Sync</Label>
          <Select value={filters.sync_status} onValueChange={(v) => setF("sync_status", v)}>
            <SelectTrigger className="rounded-none h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua</SelectItem>
              <SelectItem value="matched">Match</SelectItem>
              <SelectItem value="mismatch">Mismatch</SelectItem>
              <SelectItem value="not_synced">Belum</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase">Distrik</Label>
          <Select value={filters.distrik_id || "all"} onValueChange={(v) => setF("distrik_id", v === "all" ? "" : v)}>
            <SelectTrigger className="rounded-none h-9"><SelectValue placeholder="Semua" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua</SelectItem>
              {districts.map((d) => (
                <SelectItem key={d.id} value={d.id}>{d.kode} - {d.nama}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase">Pelanggan</Label>
          <Input placeholder="Nama..." value={filters.pelanggan} onChange={(e) => setF("pelanggan", e.target.value)} className="rounded-none h-9" />
        </div>
        <div>
          <Label className="text-[10px] uppercase">No SO</Label>
          <Input placeholder="SO..." value={filters.no_so} onChange={(e) => setF("no_so", e.target.value)} className="rounded-none h-9" />
        </div>
        <div>
          <Label className="text-[10px] uppercase">Group by</Label>
          <Select value={filters.group_by} onValueChange={(v) => setF("group_by", v)}>
            <SelectTrigger className="rounded-none h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Tidak</SelectItem>
              <SelectItem value="pelanggan">Pelanggan</SelectItem>
              <SelectItem value="distrik">Distrik</SelectItem>
              <SelectItem value="so">Sales Order</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex justify-between items-center flex-wrap gap-2">
        <div className="flex gap-2">
          <Button onClick={load} className="rounded-none" data-testid="sync-match-run">
            <MagnifyingGlass size={14} className="mr-1" /> Terapkan
          </Button>
          <Button variant="outline" onClick={() => { setFilters(emptyFilters); }} className="rounded-none">
            <ArrowClockwise size={14} className="mr-1" /> Reset
          </Button>
        </div>
        {/* Summary chips */}
        <div className="flex gap-2 text-xs">
          <span className="border border-border px-2 py-1">Total: <strong>{data.counts?.total ?? 0}</strong></span>
          <span className="bg-emerald-100 text-emerald-800 border border-emerald-300 px-2 py-1">
            <CheckCircle size={12} className="inline mr-1" /> {data.counts?.matched ?? 0}
          </span>
          <span className="bg-red-100 text-red-800 border border-red-300 px-2 py-1">
            <X size={12} className="inline mr-1" /> {data.counts?.mismatch ?? 0}
          </span>
          <span className="bg-secondary text-muted-foreground border border-border px-2 py-1">
            Belum: {data.counts?.not_synced ?? 0}
          </span>
        </div>
      </div>

      {/* Groups (if any) */}
      {data.groups?.length > 0 && (
        <div className="border border-border" data-testid="sync-match-groups">
          <div className="p-2 bg-secondary overline">Ringkasan per {filters.group_by}</div>
          <table className="w-full text-sm">
            <thead className="bg-secondary/50 text-left">
              <tr>
                <th className="p-2 overline">Grup</th>
                <th className="p-2 overline">Total SPB</th>
                <th className="p-2 overline text-emerald-800">Match</th>
                <th className="p-2 overline text-red-800">Mismatch</th>
                <th className="p-2 overline">Belum</th>
                <th className="p-2 overline">Jumlah SPB</th>
                <th className="p-2 overline">Termuat</th>
              </tr>
            </thead>
            <tbody>
              {data.groups.map((g) => (
                <tr key={g.key} className="border-t border-border">
                  <td className="p-2 font-mono">{g.key}</td>
                  <td className="p-2 font-mono">{g.total_spb}</td>
                  <td className="p-2 font-mono text-emerald-700">{g.matched}</td>
                  <td className="p-2 font-mono text-red-700">{g.mismatch}</td>
                  <td className="p-2 font-mono">{g.not_synced}</td>
                  <td className="p-2 font-mono">{g.total_jumlah?.toLocaleString("id-ID")}</td>
                  <td className="p-2 font-mono">{g.total_termuat?.toLocaleString("id-ID")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail rows */}
      <div className="border border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-left sticky top-0">
            <tr>
              <th className="p-2 overline">No SPB</th>
              <th className="p-2 overline">Tanggal</th>
              <th className="p-2 overline">Pelanggan</th>
              <th className="p-2 overline">Distrik</th>
              <th className="p-2 overline">No Polisi</th>
              <th className="p-2 overline">Produk</th>
              <th className="p-2 overline">Jumlah</th>
              <th className="p-2 overline">Termuat</th>
              <th className="p-2 overline">No SO</th>
              <th className="p-2 overline">Sync</th>
              <th className="p-2 overline">Sinkron</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={11} className="p-6 text-center overline">Memuat...</td></tr>
            ) : data.rows?.length === 0 ? (
              <tr><td colSpan={11} className="p-6 text-center text-muted-foreground">Tidak ada data.</td></tr>
            ) : (data.rows || []).map((r) => (
              <tr key={r.id} className="border-t border-border hover:bg-secondary/40" data-testid={`sync-match-row-${r.no_spb}`}>
                <td className="p-2 font-mono font-bold">
                  <Link className="text-primary hover:underline" to={`/spb/${r.id}`}>{r.no_spb}</Link>
                </td>
                <td className="p-2 font-mono text-xs">{formatIdDate(r.tanggal)}</td>
                <td className="p-2">{r.pelanggan}</td>
                <td className="p-2 text-xs">{r.distrik_kode} - {r.distrik_nama}</td>
                <td className="p-2 font-mono font-bold">{r.no_polisi}</td>
                <td className="p-2">{r.produk_nama}</td>
                <td className="p-2 font-mono">{r.jumlah}</td>
                <td className="p-2 font-mono">
                  {r.jumlah_termuat != null ? (
                    <span className={Number(r.jumlah_termuat) !== Number(r.jumlah) ? "text-orange-600 font-bold" : ""}>
                      {r.jumlah_termuat}
                    </span>
                  ) : "—"}
                </td>
                <td className="p-2 font-mono text-xs">{r.no_so || "—"}</td>
                <td className="p-2">{badge(r.sync_status, r.mismatch_reasons)}</td>
                <td className="p-2 font-mono text-[10px] text-muted-foreground">
                  {r.synced_at ? new Date(r.synced_at).toLocaleString("id-ID") : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
